/**
 * Lanificio: "Eigen maat" op de productpagina van een kleed
 * (blocks/plf-kleden-custom-size.liquid).
 *
 * - Knop "Eigen maat" onder de drie standaardmaten. Gekozen: de velden gaan
 *   open, de standaardmaat toont niet meer als gekozen, de normale prijs en
 *   het aantal verdwijnen. Een klik op een standaardmaat zet het terug.
 * - Tijdens het typen (300 ms vertraging) rekent window.LanMaatprijs.calc
 *   (assets/lan-maatprijs.js) de prijs uit. Die staat op de plek van de
 *   normale prijs, ook in de sticky bestelbalk.
 * - Ongeldig: rustige melding onder het veld, bestelknop uit.
 *   Boven € 5.990 of buiten de maatgrenzen: offerteblok, bestelknop uit.
 * - "Bestel op maat" voegt de variant van maatwerk-vloerkleed toe met precies
 *   de berekende prijs (gekoppeld op prijs via /products/maatwerk-vloerkleed.js),
 *   aantal 1, met Ontwerp, Maat, Uitvoering en de verborgen _ontwerp_handle en
 *   _ontwerp_afbeelding. De winkelwagen wordt bijgewerkt zoals bij het
 *   proefstuk (assets/lan-proefstuk.js) en de cart drawer gaat open.
 * - Resultaatkaart in het paneel: maatschets op schaal (met de standaardmaat
 *   als stippellijn), maat, m² en prijs. Prijzen tellen soepel naar het
 *   nieuwe bedrag (niet bij prefers-reduced-motion). De bestelknop toont
 *   de prijs ook: "Bestel op maat · € 1.890".
 * - Offerte (te groot of buiten de grenzen): "Vraag een offerte aan" opent
 *   een pop-up met ontwerp en maat al ingevuld; alleen e-mail is verplicht.
 *   Het formulier gaat via de gewone Shopify-route (de hCaptcha van Shopify
 *   weigert verzenden op de achtergrond met 403). Na het herladen
 *   (?contact_posted=true) opent de pop-up vanzelf met de bevestiging.
 */

const OFFERTE_KEY = 'lan-offerte-verstuurd';
import { CartLinesUpdateEvent } from '@shopify/events';

const DEBOUNCE_MS = 300;
const ERROR_TEXT = 'Dat lukte niet. Probeer het nog eens.';

let maatwerkProduct = null;
async function getMaatwerkProduct(url) {
  if (!maatwerkProduct) {
    maatwerkProduct = fetch(url, { credentials: 'same-origin' }).then((response) => {
      if (!response.ok) throw new Error('maatwerk');
      return response.json();
    });
    maatwerkProduct.catch(() => {
      maatwerkProduct = null;
    });
  }
  return maatwerkProduct;
}

function formatEuro(amount) {
  return '€ ' + amount.toLocaleString('nl-NL');
}

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Telt het bedrag in el soepel van het vorige naar het nieuwe bedrag. */
function tweenEuro(el, to) {
  const from = Number(el.dataset.amount) || 0;
  el.dataset.amount = String(to);
  cancelAnimationFrame(Number(el.dataset.raf) || 0);
  if (!from || from === to || reduceMotion.matches) {
    el.textContent = formatEuro(to);
    return;
  }
  const start = performance.now();
  const duration = 450;
  const step = (now) => {
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    const value = t < 1 ? Math.round((from + (to - from) * eased) / 10) * 10 : to;
    el.textContent = formatEuro(value);
    if (t < 1) el.dataset.raf = String(requestAnimationFrame(step));
  };
  el.dataset.raf = String(requestAnimationFrame(step));
}

function clearAmount(el) {
  cancelAnimationFrame(Number(el.dataset.raf) || 0);
  delete el.dataset.amount;
}

// Maatschets in een vak van 120: de langste zijde (minstens 360 cm, zodat
// gangbare maten en de standaardmaat goed leesbaar blijven) = 112 eenheden.
function sketchScale(longest) {
  return 112 / Math.max(360, longest || 0);
}
function sketchTransform(w, h, k) {
  return `translate(60px, 60px) scale(${(w * k).toFixed(3)}, ${(h * k).toFixed(3)})`;
}

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

async function getCart() {
  const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { credentials: 'same-origin' });
  if (!response.ok) throw new Error('cart');
  return response.json();
}

function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
  if (drawer && typeof drawer.open === 'function' && !drawer.isOpen) drawer.open();
}

class Maatwerk {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.scope = root.closest('.shopify-section') || document.body;
    this.toggle = root.querySelector('[data-lan-maatwerk-toggle]');
    this.panel = root.querySelector('[data-lan-maatwerk-panel]');
    this.error = root.querySelector('[data-lan-maatwerk-error]');
    this.quote = root.querySelector('[data-lan-maatwerk-quote]');
    this.quoteLink = root.querySelector('[data-lan-maatwerk-quote-link]');
    this.inputs = [...root.querySelectorAll('[data-lan-maat]')];
    this.card = root.querySelector('[data-lan-maatwerk-card]');
    if (this.card) {
      this.cardShape = this.card.querySelector('[data-lan-maatwerk-shape]');
      this.cardSize = this.card.querySelector('[data-lan-maatwerk-size]');
      this.cardArea = this.card.querySelector('[data-lan-maatwerk-area]');
      this.cardPrice = this.card.querySelector('[data-lan-maatwerk-price]');
      this.cardRef = this.card.querySelector('[data-lan-maatwerk-ref]');
      this.refSize = root.dataset.shape === 'round' ? [250, 250] : [200, 300];
      this.cardRef.style.transform = sketchTransform(...this.refSize, sketchScale(0));
      this.cardShape.style.transform = sketchTransform(0, 0, sketchScale(0));
    }
    this.shape = root.dataset.shape;
    this.prices = JSON.parse(root.dataset.prices || '[]');
    this.active = false;
    this.result = null;
    this.busy = false;
    this.timer = 0;

    this.toggle.addEventListener('click', () => (this.active ? this.deactivate() : this.activate()));
    this.quoteLink?.addEventListener('click', (event) => {
      if (this.openOfferte()) event.preventDefault();
    });
    this.inputs.forEach((input) => {
      input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.update(), DEBOUNCE_MS);
      });
      input.addEventListener('change', () => {
        clearTimeout(this.timer);
        this.update();
      });
    });

    // Een standaardmaat kiezen zet de eigen maat uit.
    this.scope.addEventListener('click', (event) => {
      if (this.active && event.target instanceof Element && event.target.closest('.variant-option__button-label')) {
        this.deactivate();
      }
    });

    // Vóór de handlers van het thema: bestelknop (en de sticky balk, die de
    // bestelknop aanklikt) en het formulier zelf.
    window.addEventListener('click', (event) => this.onClick(event), true);
    window.addEventListener('submit', (event) => this.onSubmit(event), true);
  }

  get form() {
    return this.scope.querySelector('form[data-type="add-to-cart-form"]');
  }

  get addButton() {
    return this.form?.querySelector('[ref="addToCartButton"]') || null;
  }

  activate() {
    this.active = true;
    this.toggle.setAttribute('aria-pressed', 'true');
    this.panel.hidden = false;
    this.scope.classList.add('lan-maatwerk-actief');
    this.update();
    this.inputs[0]?.focus({ preventScroll: true });
  }

  deactivate() {
    this.active = false;
    clearTimeout(this.timer);
    this.toggle.setAttribute('aria-pressed', 'false');
    this.panel.hidden = true;
    this.scope.classList.remove('lan-maatwerk-actief');
    if (this.priceBox) this.priceBox.hidden = true;
    this.setButtonPrice(null);
    this.setButtonDisabled(false);
    this.restoreSticky();
  }

  /** Prijsvak direct na de normale prijs (opnieuw aanmaken als het thema het weghaalde). */
  ensurePriceBox() {
    if (this.priceBox?.isConnected) return this.priceBox;
    const priceEl = this.scope.querySelector('product-price');
    const box = document.createElement('div');
    box.className = 'lan-maat-prijs';
    box.innerHTML =
      '<p class="lan-maat-prijs__bedrag" aria-live="polite"></p><p class="lan-maat-prijs__note"></p>';
    box.querySelector('.lan-maat-prijs__note').textContent = this.root.dataset.priceNote || '';
    if (priceEl) priceEl.after(box);
    else this.root.before(box);
    this.priceBox = box;
    return box;
  }

  values() {
    const out = {};
    this.inputs.forEach((input) => {
      out[input.dataset.lanMaat] = input.value.trim() === '' ? null : Number(input.value);
    });
    return out;
  }

  update() {
    if (!this.active) return;
    const values = this.values();
    const filled = Object.values(values).every((v) => v !== null);
    const calc = window.LanMaatprijs?.calc;

    this.inputs.forEach((input) => input.removeAttribute('aria-invalid'));
    this.error.hidden = true;
    this.quote.hidden = true;

    let result = null;
    if (filled && calc) {
      result = calc({ round: this.shape === 'round', prices: this.prices, ...values });
    }
    this.result = result;
    this.renderCard(values, result);
    this.setButtonPrice(result && result.status === 'ok' ? result.price : null);

    const box = this.ensurePriceBox();
    box.hidden = false;
    const amount = box.querySelector('.lan-maat-prijs__bedrag');
    const note = box.querySelector('.lan-maat-prijs__note');

    if (!result) {
      clearAmount(amount);
      amount.textContent = 'Vul je maat in voor de prijs';
      amount.classList.add('lan-maat-prijs__bedrag--leeg');
      note.hidden = true;
      this.setButtonDisabled(true);
      this.updateSticky(null);
      return;
    }

    if (result.status === 'invalid') {
      this.error.textContent = result.message;
      this.error.hidden = false;
      this.root.querySelector(`[data-lan-maat="${result.field}"]`)?.setAttribute('aria-invalid', 'true');
      clearAmount(amount);
      amount.textContent = 'Vul een geldige maat in';
      amount.classList.add('lan-maat-prijs__bedrag--leeg');
      note.hidden = true;
      this.setButtonDisabled(true);
      this.updateSticky(null);
      return;
    }

    if (result.status === 'quote') {
      this.quote.hidden = false;
      this.quoteLink.href = this.quoteUrl(values);
      clearAmount(amount);
      amount.textContent = 'Prijs op offerte';
      amount.classList.add('lan-maat-prijs__bedrag--leeg');
      note.hidden = true;
      this.setButtonDisabled(true);
      this.updateSticky(null);
      return;
    }

    amount.classList.remove('lan-maat-prijs__bedrag--leeg');
    tweenEuro(amount, result.price);
    note.hidden = !note.textContent;
    this.setButtonDisabled(false);
    this.updateSticky(result);
  }

  renderCard(values, result) {
    if (!this.card) return;
    const round = this.shape === 'round';
    const clamp = (v) => (v > 0 ? Math.min(v, 600) : 0);
    let w;
    let h;
    if (round) {
      w = h = clamp(values.diameter);
    } else {
      const a = clamp(values.width);
      const b = clamp(values.length);
      w = a && b ? Math.min(a, b) : a || b;
      h = a && b ? Math.max(a, b) : a || b;
    }
    const k = sketchScale(h);
    this.cardShape.style.transform = sketchTransform(w, h, k);
    this.cardRef.style.transform = sketchTransform(...this.refSize, k);

    let state = 'ok';
    if (!result) state = 'leeg';
    else if (result.status === 'invalid') state = 'fout';
    else if (result.status === 'quote') state = 'offerte';
    if (state === 'leeg' && w) state = 'fout';
    this.card.dataset.state = state;

    const price = this.cardPrice;
    price.classList.toggle('lan-maat-kaart__prijs--klein', state !== 'ok');
    if (state === 'leeg') {
      this.cardSize.textContent = 'Vul je maat in';
      this.cardArea.textContent = '';
      clearAmount(price);
      price.textContent = '';
      return;
    }
    if (result && result.label) {
      this.cardSize.textContent = result.label.replace(' x ', ' × ');
      this.cardArea.textContent = result.area ? result.area.toLocaleString('nl-NL', { maximumFractionDigits: 2 }) + ' m²' : '';
    } else {
      this.cardSize.textContent = round ? 'Ø …' : (w || '…') + ' × ' + (h && h !== w ? h : '…') + ' cm';
      this.cardArea.textContent = '';
    }
    if (state === 'ok') {
      tweenEuro(price, result.price);
    } else {
      clearAmount(price);
      price.textContent = state === 'offerte' ? 'Voor deze maat maken we een offerte.' : 'Nog geen prijs: controleer je maat.';
    }
  }

  sizeLabel(values) {
    if (this.shape === 'round') return 'Ø ' + values.diameter + ' cm';
    return Math.min(values.width, values.length) + ' x ' + Math.max(values.width, values.length) + ' cm';
  }

  /** Zet de prijs in de bestelknop ("Bestel op maat · € 1.890"), of haalt hem weg. */
  setButtonPrice(price) {
    const button = this.addButton;
    if (!button) return;
    const node = [...button.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
    if (!node) return;
    if (this.buttonText == null) this.buttonText = node.textContent.trim();
    node.textContent = price ? `${this.buttonText} · ${formatEuro(price)}` : this.buttonText;
    // De prijs van de gekozen standaardmaat (snippets/add-to-cart-button.liquid) zolang de eigen maat geldt verbergen.
    const variantPrice = button.querySelector('[data-lan-knopprijs]');
    if (variantPrice) variantPrice.hidden = Boolean(price);
  }

  /** Opent de offerte-pop-up. Geeft false als die er niet is (dan werkt de gewone link). */
  openOfferte() {
    if (!this.offerte) {
      const template = this.root.querySelector('[data-lan-offerte-template]');
      const dialog = template?.content.querySelector('dialog');
      if (!dialog || typeof dialog.showModal !== 'function') return false;
      this.offerte = document.importNode(dialog, true);
      document.body.append(this.offerte);
      this.wireOfferte(this.offerte);
    }
    const dialog = this.offerte;
    const values = this.values();
    if (Object.values(values).every((v) => v !== null)) {
      const label = this.sizeLabel(values).replace(' x ', ' × ');
      dialog.querySelector('[data-lan-offerte-size]').textContent = label;
      dialog.querySelector('[data-lan-offerte-maat]').value = label;
    }
    dialog.querySelector('[data-lan-offerte-step]').hidden = false;
    dialog.querySelector('[data-lan-offerte-done]').hidden = true;
    dialog.querySelector('[data-lan-offerte-error]').hidden = true;
    dialog.showModal();
    dialog.querySelector('[data-lan-offerte-email]').focus({ preventScroll: true });
    return true;
  }

  /** @param {HTMLDialogElement} dialog */
  wireOfferte(dialog) {
    const form = dialog.querySelector('form');
    const email = dialog.querySelector('[data-lan-offerte-email]');
    const error = dialog.querySelector('[data-lan-offerte-error]');
    const submit = dialog.querySelector('[data-lan-offerte-submit]');

    dialog.querySelectorAll('[data-lan-offerte-close]').forEach((button) =>
      button.addEventListener('click', () => dialog.close())
    );
    // Klik naast de pop-up sluit hem.
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    email.addEventListener('input', () => email.removeAttribute('aria-invalid'));

    // Eigen controle vóór de handler van Shopify (capture), daarna gaat het
    // formulier gewoon door; de bevestiging volgt na het herladen.
    form.addEventListener(
      'submit',
      (event) => {
        error.hidden = true;
        if (!email.value.trim() || !email.checkValidity()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          email.setAttribute('aria-invalid', 'true');
          error.textContent = 'Vul een geldig e-mailadres in.';
          error.hidden = false;
          email.focus();
          return;
        }
        submit.setAttribute('aria-busy', 'true');
        try {
          window.sessionStorage.setItem(
            OFFERTE_KEY,
            JSON.stringify({
              handle: this.root.dataset.handle,
              size: dialog.querySelector('[data-lan-offerte-maat]').value,
              mail: email.value.trim(),
            })
          );
        } catch (err) {
          /* zonder sessionStorage geen bevestiging, versturen werkt wel */
        }
      },
      true
    );
  }

  /** Na het versturen (pagina herladen met ?contact_posted=true): bevestiging tonen. */
  showOfferteDone() {
    let sent = null;
    try {
      sent = JSON.parse(window.sessionStorage.getItem(OFFERTE_KEY) || 'null');
    } catch (err) {
      return;
    }
    if (!sent || sent.handle !== this.root.dataset.handle) return;
    if (!/[?&]contact_posted=true/.test(window.location.search)) return;
    window.sessionStorage.removeItem(OFFERTE_KEY);
    if (!this.openOfferte()) return;
    const dialog = this.offerte;
    dialog.querySelector('[data-lan-offerte-done-size]').textContent = sent.size;
    dialog.querySelector('[data-lan-offerte-done-mail]').textContent = sent.mail;
    dialog.querySelector('[data-lan-offerte-step]').hidden = true;
    dialog.querySelector('[data-lan-offerte-done]').hidden = false;
    dialog.querySelector('[data-lan-offerte-done] [data-lan-offerte-close]')?.focus({ preventScroll: true });
  }

  quoteUrl(values) {
    const label = this.sizeLabel(values);
    const vorm = { round: 'Rond', organic: 'Organisch', rect: 'Rechthoekig' }[this.shape];
    const params = new URLSearchParams({
      bron: 'maat',
      kleed: this.root.dataset.title,
      kleed_handle: this.root.dataset.handle,
      afmeting: label,
      vorm,
    });
    return this.root.dataset.quoteUrl + '?' + params.toString();
  }

  setButtonDisabled(disabled) {
    const button = this.addButton;
    if (!button) return;
    if (disabled) {
      if (!button.disabled) {
        button.disabled = true;
        this.disabledByUs = true;
      }
    } else if (this.disabledByUs) {
      button.disabled = false;
      this.disabledByUs = false;
    }
    const sticky = document.querySelector('sticky-add-to-cart [ref="addToCartButton"]');
    if (sticky) sticky.disabled = button.disabled;
  }

  updateSticky(result) {
    const variant = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__variant');
    const price = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__price');
    if (!variant || !price) return;
    if (!this.stickyOriginal) this.stickyOriginal = { variant: variant.innerHTML, price: price.innerHTML };
    variant.textContent = result ? result.label + ', op maat' : 'Eigen maat';
    price.innerHTML = `<div><span class="price">${result ? formatEuro(result.price) : ''}</span></div>`;
  }

  restoreSticky() {
    if (!this.stickyOriginal) return;
    const variant = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__variant');
    const price = document.querySelector('sticky-add-to-cart .sticky-add-to-cart__price');
    if (variant) variant.innerHTML = this.stickyOriginal.variant;
    if (price) price.innerHTML = this.stickyOriginal.price;
    this.stickyOriginal = null;
  }

  onClick(event) {
    if (!this.active || !(event.target instanceof Element)) return;
    const button = event.target.closest('[ref="addToCartButton"]');
    if (!button || button !== this.addButton) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.add();
  }

  onSubmit(event) {
    if (!this.active || event.target !== this.form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.add();
  }

  async add() {
    const result = this.result;
    if (this.busy || !result || result.status !== 'ok') return;
    this.busy = true;
    const button = this.addButton;
    button?.setAttribute('aria-busy', 'true');

    try {
      const product = await getMaatwerkProduct(this.root.dataset.maatwerkUrl);
      const variant = product.variants.find((v) => v.price === result.price * 100 && v.available);
      if (!variant) throw new Error('Geen maatwerkvariant voor ' + result.price);

      const properties = {
        Ontwerp: this.root.dataset.title,
        Maat: result.label,
        Uitvoering: 'Op maat',
        _ontwerp_handle: this.root.dataset.handle,
      };
      if (this.root.dataset.image) properties._ontwerp_afbeelding = this.root.dataset.image;

      const deferred = CartLinesUpdateEvent.createPromise();
      this.root.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
          promise: deferred.promise,
        })
      );

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variant.id, quantity: 1, properties }],
          sections: cartSectionIds().join(','),
        }),
      });
      const data = await response.json();
      const cart = await getCart();

      if (!response.ok || data.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, source: 'lan-maatwerk' },
        });
        throw new Error(data.description || data.message || ERROR_TEXT);
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'lan-maatwerk', itemCount: 1, sections: data.sections, didError: false },
      });
      openCartDrawer();
    } catch (error) {
      console.warn('Lanificio eigen maat:', error);
      this.error.textContent = ERROR_TEXT;
      this.error.hidden = false;
    } finally {
      this.busy = false;
      button?.removeAttribute('aria-busy');
    }
  }
}

function init() {
  document.querySelectorAll('[data-lan-maatwerk]').forEach((root) => {
    if (root instanceof HTMLElement && !root.dataset.lanMaatwerkReady) {
      root.dataset.lanMaatwerkReady = 'true';
      new Maatwerk(root).showOfferteDone();
    }
  });
}

init();
document.addEventListener('shopify:section:load', init);
